// TMOS Content Management Service
// Date: January 27, 2025
// Purpose: Frontend service for managing website content (admin only)

import api from './api';

export interface PrivacyPolicy {
    id: string;
    content_html: string;
    version: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    created_by?: string;
}

export interface TermsOfService {
    id: string;
    content_html: string;
    version: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    created_by?: string;
}

export interface SocialLink {
    id: string;
    platform_name: string;
    url: string;
    icon_name?: string;
    display_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface ProductContentSection {
    id: string;
    section_key: 'overview' | 'features' | 'security' | 'integrations';
    title?: string;
    content_html: string;
    version: string;
    display_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    created_by?: string;
}

export interface Solution {
    id: string;
    title: string;
    description?: string;
    image_url?: string;
    category: 'generic' | 'industry_specific';
    industry?: string;
    marketplace_template_id?: string;
    display_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    created_by?: string;
}

export interface WhyTMOSContent {
    id: string;
    tab_key: 'pe_companies' | 'pe_backed_companies' | 'public_companies' | 'consultants' | 'tmos_difference' | 'tmos_solutions' | 'tmos_unique_features';
    title: string;
    description?: string;
    content_html: string;
    display_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    created_by?: string;
}

export interface Template {
    id: string;
    name: string;
    description?: string;
    marketplace_visibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC';
}

export interface HomepageContent {
    id: string;
    content_key:
        | 'hero_title'
        | 'hero_subtitle'
        | 'organization_title'
        | 'organization_subtitle'
        | 'approach_title'
        | 'approach_body'
        | 'how_title'
        | 'how_body'
        | 'impact_label'
        | 'impact_headline'
        | 'impact_card_1'
        | 'impact_card_2'
        | 'impact_card_3'
        | 'cta_email_placeholder'
        | 'cta_get_started'
        | 'cta_try_ai'
        | 'compare_title'
        | 'compare_row_multi_tenant'
        | 'compare_row_self_service'
        | 'compare_row_ai_planning'
        | 'compare_row_marketplace'
        | 'compare_row_full_os'
        | 'compare_others_mostly_no'
        | 'compare_others_sometimes'
        | 'compare_others_rare'
        | 'who_cards'
        | 'compare_rows';
    content_value: string;
    display_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    created_by?: string;
}

export interface PublicNavigationItem {
    id: string;
    item_key: 'why_tmos' | 'product' | 'solutions' | 'marketplace';
    label: string;
    path: string;
    display_order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

class ContentManagementService {
    // =====================================================
    // PRIVACY POLICY
    // =====================================================

    async getPrivacyPolicyHistory(): Promise<PrivacyPolicy[]> {
        const response = await api.get('/admin/content/privacy-policy/history');
        return response.data.data || [];
    }

    async createOrUpdatePrivacyPolicy(data: {
        id?: string;
        content_html: string;
        version: string;
    }): Promise<PrivacyPolicy> {
        const response = await api.post('/admin/content/privacy-policy', data);
        return response.data.data;
    }

    // =====================================================
    // TERMS OF SERVICE
    // =====================================================

    async getTermsOfServiceHistory(): Promise<TermsOfService[]> {
        const response = await api.get('/admin/content/terms-of-service/history');
        return response.data.data || [];
    }

    async createOrUpdateTermsOfService(data: {
        id?: string;
        content_html: string;
        version: string;
    }): Promise<TermsOfService> {
        const response = await api.post('/admin/content/terms-of-service', data);
        return response.data.data;
    }

    // =====================================================
    // SOCIAL LINKS
    // =====================================================

    async getAllSocialLinks(): Promise<SocialLink[]> {
        const response = await api.get('/admin/content/social-links');
        return response.data.data || [];
    }

    async createSocialLink(data: {
        platform_name: string;
        url: string;
        icon_name?: string;
        display_order?: number;
    }): Promise<SocialLink> {
        const response = await api.post('/admin/content/social-links', data);
        return response.data.data;
    }

    async updateSocialLink(id: string, data: Partial<SocialLink>): Promise<SocialLink> {
        const response = await api.put(`/admin/content/social-links/${id}`, data);
        return response.data.data;
    }

    async deleteSocialLink(id: string): Promise<void> {
        await api.delete(`/admin/content/social-links/${id}`);
    }

    // =====================================================
    // PRODUCT CONTENT SECTIONS
    // =====================================================

    async getAllProductSections(): Promise<ProductContentSection[]> {
        const response = await api.get('/admin/content/product-sections');
        return response.data.data || [];
    }

    async updateProductSection(
        sectionKey: string,
        data: {
            content_html: string;
            version: string;
            title?: string;
            display_order?: number;
        }
    ): Promise<ProductContentSection> {
        const response = await api.put(`/admin/content/product-sections/${sectionKey}`, data);
        return response.data.data;
    }

    // =====================================================
    // SOLUTIONS
    // =====================================================

    async getAllSolutions(): Promise<Solution[]> {
        const response = await api.get('/admin/content/solutions');
        return response.data.data || [];
    }

    async createSolution(data: {
        title: string;
        description?: string;
        image_url?: string;
        category: 'generic' | 'industry_specific';
        industry?: string;
        marketplace_template_id?: string;
        display_order?: number;
    }): Promise<Solution> {
        const response = await api.post('/admin/content/solutions', data);
        return response.data.data;
    }

    async updateSolution(id: string, data: Partial<Solution>): Promise<Solution> {
        const response = await api.put(`/admin/content/solutions/${id}`, data);
        return response.data.data;
    }

    async deleteSolution(id: string): Promise<void> {
        await api.delete(`/admin/content/solutions/${id}`);
    }

    // =====================================================
    // WHY T-MOS CONTENT
    // =====================================================

    async getAllWhyTMOSTabs(): Promise<WhyTMOSContent[]> {
        const response = await api.get('/admin/content/why-tmos-tabs');
        return response.data.data || [];
    }

    async updateWhyTMOSTab(
        tabKey: string,
        data: {
            title: string;
            description?: string;
            content_html: string;
            display_order?: number;
        }
    ): Promise<WhyTMOSContent> {
        const response = await api.put(`/admin/content/why-tmos-tabs/${tabKey}`, data);
        return response.data.data;
    }

    // =====================================================
    // TEMPLATES (for solution marketplace links)
    // =====================================================

    async getMarketplaceTemplates(): Promise<Template[]> {
        const response = await api.get('/templates/marketplace');
        return response.data.data?.templates || [];
    }

    // =====================================================
    // HOMEPAGE CONTENT
    // =====================================================

    async getAllHomepageContent(): Promise<HomepageContent[]> {
        const response = await api.get('/admin/content/homepage');
        return response.data.data || [];
    }

    async updateHomepageContent(contentKey: string, contentValue: string): Promise<HomepageContent> {
        const response = await api.put('/admin/content/homepage', {
            content_key: contentKey,
            content_value: contentValue
        });
        return response.data.data;
    }

    // =====================================================
    // PUBLIC NAVIGATION ITEMS
    // =====================================================

    async getAllNavigationItems(): Promise<PublicNavigationItem[]> {
        const response = await api.get('/admin/content/navigation-items');
        return response.data.data || [];
    }

    async toggleNavigationItem(id: string, isActive: boolean): Promise<PublicNavigationItem> {
        const response = await api.patch(`/admin/content/navigation-items/${id}/toggle`, {
            is_active: isActive
        });
        return response.data.data;
    }

    async updateNavigationItem(itemKey: string, data: Partial<PublicNavigationItem>): Promise<PublicNavigationItem> {
        const response = await api.put(`/admin/content/navigation-items/${itemKey}`, data);
        return response.data.data;
    }
}

export const contentManagementService = new ContentManagementService();

